/**
 * Token_Handler.ts
 * Lightweight wrapper around AsyncStorage for persisting non-sensitive UI data
 * (orgId, business name, user display name). Auth tokens are httpOnly cookies —
 * never stored here.
 *
 * Keys are NAMESPACED by the product the user arrived from (from the handoff),
 * so the platform's stored UI data reflects the originating product — e.g.
 * `sk_upsell_org_id` when the user came from SK Upsell. A direct visit (no
 * handoff) falls back to `sk_platform_*`.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { restoreHandoff } from '../navigation/productHandoff';

const FIELDS = ['org_id', 'business_name', 'first_name', 'last_name'] as const;
type Field = (typeof FIELDS)[number];

/** Prefix derived from the originating product; `sk_platform` when none. */
function prefix(): string {
  const product = restoreHandoff()?.product;
  return `sk_${product || 'platform'}`;
}

const keyFor = (field: Field): string => `${prefix()}_${field}`;

// Prefixes cleared on logout so a sign-out never leaves stale display data
// behind, regardless of which product the session came from. (Non-sensitive UI
// data — orphans would be harmless, but we clean up anyway.) `sk_catalog` is
// kept for backward-compat with the previous hardcoded keys.
const CLEAR_PREFIXES = [
  'sk_platform',
  'sk_catalog',
  'sk_upsell',
  'sk_voice',
  'sk_crm',
  'sk_cal',
  'sk_chat',
  'sk_works',
  'sk_tickets',
  'sk_inventory',
];

export const Token_Handler = {
  setOrgId: (v: string) => AsyncStorage.setItem(keyFor('org_id'), v),
  getOrgId: () => AsyncStorage.getItem(keyFor('org_id')),
  setBusinessName: (v: string) => AsyncStorage.setItem(keyFor('business_name'), v),
  getBusinessName: () => AsyncStorage.getItem(keyFor('business_name')),
  setFirstName: (v: string) => AsyncStorage.setItem(keyFor('first_name'), v),
  getFirstName: () => AsyncStorage.getItem(keyFor('first_name')),
  setLastName: (v: string) => AsyncStorage.setItem(keyFor('last_name'), v),
  getLastName: () => AsyncStorage.getItem(keyFor('last_name')),
  clearAll: async () => {
    const keys = CLEAR_PREFIXES.flatMap((p) => FIELDS.map((f) => `${p}_${f}`));
    await Promise.all(keys.map((k) => AsyncStorage.removeItem(k)));
  },
};
